import type { Deal } from '@/constants/crm'

import { useMemo, useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Tag from '@/components/ui/tag'
import StatCard from '@/components/generals/stat-card'
import CompanyMark from '@/components/generals/company-mark'
import { EmptyState } from '@/components/generals/view-parts'
import { ownerByName } from '@/constants/companies'
import { stageMeta } from '@/constants/crm'
import { formatCompactMoney, formatMoney } from '@/lib/companies'
import { apiRequest } from '@/lib/client-api'
import { indexById, isSlipping } from '@/lib/crm'
import { addDays, daysBetween, formatShortDate } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'

export default function SlippingDeals() {
    const today = useCrmStore(state => state.today)
    const status = useCrmStore(state => state.status)
    const deals = useCrmStore(state => state.deals)
    const companies = useCrmStore(state => state.companies)
    const upsertDeal = useCrmStore(state => state.upsertDeal)
    const openDialog = useCrmStore(state => state.openDialog)
    const openDetail = useCrmStore(state => state.openDetail)
    const showNotice = useCrmStore(state => state.showNotice)
    const [busyId, setBusyId] = useState<string | null>(null)

    const companyIndex = useMemo(() => indexById(companies), [companies])
    const slipping = useMemo(
        () => deals
            .filter(deal => isSlipping(deal, today))
            .sort((a, b) => a.closeDate.localeCompare(b.closeDate)),
        [deals, today]
    )
    const atRisk = slipping.reduce((sum, deal) => sum + deal.value, 0)
    const averageOverdue = slipping.length
        ? Math.round(slipping.reduce((sum, deal) => sum + daysBetween(deal.closeDate, today), 0) / slipping.length)
        : 0

    async function update(deal: Deal, patch: Partial<Pick<Deal, 'stage' | 'closeDate'>>, message: string) {
        setBusyId(deal.id)

        const result = await apiRequest<{ deal: Deal }>(`/api/deals/${deal.id}`, 'PATCH', patch)

        setBusyId(null)

        if (!result.ok) {
            showNotice('error', result.message)

            return
        }

        upsertDeal(result.data.deal)
        showNotice('success', message)
    }

    return (
        <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
            <div className='grid grid-cols-1 gap-2 p-4 sm:grid-cols-3'>
                <StatCard label="Deals past close date" value={slipping.length} hint="Open deals whose close date has passed" />
                <StatCard label="Value at risk" value={formatCompactMoney(atRisk)} hint={`$${formatMoney(atRisk)} in open pipeline`} />
                <StatCard label="Average days late" value={averageOverdue} hint="Days since the planned close date" />
            </div>

            <div className='border-border border-t'>
                {slipping.length === 0 ? (
                    <EmptyState
                        title={status === 'loading' ? "Loading deals…" : "Nothing is slipping"}
                        description={status === 'loading' ? undefined : "Every open deal still has a close date in the future. Deals show up here as soon as their date passes."}
                    />
                ) : (
                    <div className='overflow-x-auto'>
                        <table className='w-full min-w-[880px] border-collapse text-[14px] leading-none'>
                            <thead>
                                <tr className='border-border border-b'>
                                    {["Deal", "Company", "Owner", "Stage", "Value", "Was due", "Actions"].map(label => (
                                        <th key={label} scope='col' className='caption-style text-subtle h-[38px] px-4 text-left font-normal whitespace-nowrap last:text-right'>
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {slipping.map(deal => {
                                    const company = deal.companyId ? companyIndex.get(deal.companyId) : undefined
                                    const owner = ownerByName(deal.owner)
                                    const stage = stageMeta(deal.stage)
                                    const late = daysBetween(deal.closeDate, today)
                                    const busy = busyId === deal.id

                                    return (
                                        <tr key={deal.id} className='border-border hover:bg-card/60 border-b transition-colors duration-150'>
                                            <td className='px-4 py-3'>
                                                <button
                                                    type='button'
                                                    onClick={() => openDialog('deal', { dealId: deal.id })}
                                                    className='hover:text-soft cursor-pointer text-left font-medium transition-colors duration-150'
                                                >
                                                    <span data-mdlfy-cms={`deals:${deal.id}:name`}>
                                                        {deal.name}
                                                    </span>
                                                </button>
                                            </td>
                                            <td className='px-4 py-3'>
                                                {company ? (
                                                    <button
                                                        type='button'
                                                        onClick={() => openDetail(company.id)}
                                                        className='hover:text-soft flex cursor-pointer items-center gap-2 transition-colors duration-150'
                                                    >
                                                        <CompanyMark company={company} className='size-5 rounded-[5px]' />
                                                        <span data-mdlfy-cms={`companies:${company.id}:name`}>
                                                            {company.name}
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <span className='text-subtle'>None</span>
                                                )}
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className='flex items-center gap-1.5 whitespace-nowrap'>
                                                    <Avatar src={owner.avatar} alt='' />
                                                    <span data-mdlfy-cms={`deals:${deal.id}:owner`}>
                                                        {deal.owner}
                                                    </span>
                                                </span>
                                            </td>
                                            <td className='px-4 py-3'>
                                                <Tag tone={stage.tone} size='sm' className='text-[12px]'>
                                                    {stage.value}
                                                </Tag>
                                            </td>
                                            <td className='px-4 py-3 tabular-nums whitespace-nowrap'>
                                                <span className='text-muted-foreground'>$</span>
                                                <span>{formatMoney(deal.value)}</span>
                                            </td>
                                            <td className='px-4 py-3 whitespace-nowrap'>
                                                <span className='flex flex-col gap-1.5'>
                                                    <span className='tabular-nums'>{formatShortDate(deal.closeDate)}</span>
                                                    <span className='caption-style text-danger'>
                                                        <span className='tabular-nums'>{late}</span> {late === 1 ? "day late" : "days late"}
                                                    </span>
                                                </span>
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className='flex justify-end gap-1'>
                                                    <Button
                                                        variant='secondary'
                                                        size='sm'
                                                        disabled={busy}
                                                        onClick={() => update(deal, { closeDate: addDays(today, 30) }, `${deal.name} pushed to ${formatShortDate(addDays(today, 30))}.`)}
                                                    >
                                                        Push 30 days
                                                    </Button>
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        disabled={busy}
                                                        onClick={() => update(deal, { stage: 'Closed Lost' }, `${deal.name} marked as lost.`)}
                                                    >
                                                        Mark lost
                                                    </Button>
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
